import { Link } from "react-router-dom";
import Avatar from "@/components/base/Avatar";
import { formatViews, getCategoryBySlug, getCategoryName } from "@/utils/format";

interface VideoInfoProps {
  title: string;
  author: string;
  authorType: string;
  category: string;
  views: number;
  publishDate: string;
  description: string;
  tags: string[];
  liked: boolean;
  favorited: boolean;
  onToggleLike: () => void;
  onToggleFavorite: () => void;
  onShare: () => void;
}

export default function VideoInfo({
  title,
  author,
  authorType,
  category,
  views,
  publishDate,
  description,
  tags,
  liked,
  favorited,
  onToggleLike,
  onToggleFavorite,
  onShare,
}: VideoInfoProps) {
  const cat = getCategoryBySlug(category);
  const tone = category === "entertainment" ? "accent" : category === "life" ? "secondary" : "primary";

  const actionClass =
    "inline-flex items-center gap-2 h-10 px-4 rounded-full border text-sm cursor-pointer transition-colors whitespace-nowrap";

  return (
    <div className="mt-5">
      <h1 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 leading-snug">{title}</h1>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3">
        <div className="flex items-center gap-3">
          <Avatar name={author} size="md" tone={tone} />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground-900">{author}</span>
              {authorType === "official" && (
                <span className="px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 text-xs">官方</span>
              )}
            </div>
            <span className="text-xs text-foreground-500">发布于 {publishDate}</span>
          </div>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onToggleLike}
            className={`${actionClass} ${
              liked
                ? "bg-primary-500 border-primary-500 text-background-50"
                : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
            }`}
          >
            <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true"></i>
            {liked ? "已点赞" : "点赞"}
          </button>
          <button
            type="button"
            onClick={onToggleFavorite}
            className={`${actionClass} ${
              favorited
                ? "bg-accent-500 border-accent-500 text-background-50"
                : "border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
            }`}
          >
            <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} aria-hidden="true"></i>
            {favorited ? "已收藏" : "收藏"}
          </button>
          <button
            type="button"
            onClick={onShare}
            className={`${actionClass} border-background-300 text-foreground-700 hover:border-secondary-400 hover:text-secondary-900`}
          >
            <i className="ri-share-forward-line" aria-hidden="true"></i>
            分享
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
        <span className="flex items-center gap-1">
          <i className="ri-play-circle-line" aria-hidden="true"></i>
          {formatViews(views)} 次播放
        </span>
        {cat && (
          <Link to={`/category/${cat.slug}`} className="flex items-center gap-1 hover:text-primary-600">
            <i className={cat.icon} aria-hidden="true"></i>
            {getCategoryName(category)}
          </Link>
        )}
      </div>

      <div className="mt-5 p-4 rounded-lg bg-background-100 border border-background-200">
        <h2 className="font-heading font-semibold text-foreground-950 mb-2">视频简介</h2>
        <p className="text-sm text-foreground-700 leading-relaxed">{description}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Link
              key={tag}
              to={`/search?q=${encodeURIComponent(tag)}`}
              className="h-7 px-3 inline-flex items-center rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}